import React from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";

export const RaceInfo = ({ 
  categoryName, 
  qualification, 
  raceInclusions 
}) => {
  const trimmedCategoryName = categoryName ? categoryName.trim() : "";

  // Use only props from Firebase - no fallbacks
  const qualificationList = qualification || [];
  const inclusionList = raceInclusions || [];

  // Helper to chunk array
  const chunkArray = (arr, size) => {
    if (!arr) return [];
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  const qualChunks = chunkArray(qualificationList, 2);
  const inclChunks = chunkArray(inclusionList, 3);

  // Helper to translate data-driven strings from DB
  const translateItem = (text) => {
    if (!text) return "";
    const translations = {
      // Race Inclusions
      "Nomor BIB": "BIB Number",
      "Race Jersey": "Race Jersey",
      "Medali Finisher": "Finisher Medal",
      "Jersey Finisher": "Finisher Jersey",
      "Finisher Certificate": "Finisher Certificate",
      "Asuransi": "Insurance",
      "Refreshment": "Refreshment",
      
      // Qualifications (Common terms)
      "Sehat jasmani dan rohani": "Physically and mentally fit",
      "Sudah divaksin": "Fully vaccinated",
    };
    
    // Check for "Pernah mengikuti..." pattern for ultra races
    if (text.toLowerCase().includes("pernah mengikuti")) {
      return text.replace(/pernah mengikuti/i, "Previously completed");
    }
    
    return translations[text] || text;
  };

  // Render helper for Qualification Items
  const renderQualificationItem = (item, index) => (
    <div key={index} className="inline-flex items-center gap-3 py-2 px-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 hover:border-lime-500 transition-colors">
      <div className="text-lime-600 flex-shrink-0 text-sm">
         <FontAwesomeIcon icon={faCheckCircle} />
      </div>
      <span className="text-xs font-medium">
        {translateItem(item)}
      </span>
    </div>
  );

  // Render helper for Inclusion Items
  const renderInclusionItem = (item, index) => (
    <div key={index} className="inline-flex items-center gap-2 px-3 py-1 bg-slate-50 border border-slate-200 rounded-full text-slate-700 text-xs font-medium hover:border-lime-500 transition-colors">
      <span className="w-1.5 h-1.5 rounded-full bg-lime-600"></span>
      {translateItem(item)}
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
      {/* Existing Qualification and Race Inclusions Container */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Kolom Kualifikasi (Kiri) */}
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h3 className="text-xl font-bold text-lime-600 mb-4">
            Qualification
          </h3>
          
          {qualificationList && qualificationList.length > 0 ? (
            <>
              {/* Mobile View: Standard Wrap */}
              <div className="flex flex-wrap gap-3 lg:hidden">
                {qualificationList.map((item, index) => renderQualificationItem(item, index))}
              </div>

              {/* Desktop View: Strictly Chunked (Max 2) */}
              <div className="hidden lg:block space-y-3">
                {qualChunks.map((chunk, chunkIndex) => (
                  <div key={chunkIndex} className="flex flex-wrap gap-3">
                    {chunk.map((item, index) => renderQualificationItem(item, index))}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 italic p-2 border border-dashed border-slate-300 rounded-lg">
              No specific qualifications for this category.
            </p>
          )}
        </motion.div>

        {/* Kolom Inklusi Perlombaan (Kanan) */}
        <motion.div
          className="space-y-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h3 className="text-xl font-bold text-lime-600 mb-4">
            Race Inclusions
          </h3>
          
          {inclusionList && inclusionList.length > 0 ? (
            <>
              {/* Mobile View: Standard Wrap */}
              <div className="flex flex-wrap gap-3 lg:hidden">
                {inclusionList.map((item, index) => renderInclusionItem(item, index))}
              </div>

              {/* Desktop View: Strictly Chunked (Max 3) */}
              <div className="hidden lg:block space-y-3">
                {inclChunks.map((chunk, chunkIndex) => (
                   <div key={chunkIndex} className="flex flex-wrap gap-3">
                      {chunk.map((item, index) => renderInclusionItem(item, index))}
                   </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 italic">
              No inclusion information available for this category.
            </p>
          )}
        </motion.div>
      </div>

      {/* New Container for Images (No Border) */}
      <div className="bg-white rounded-lg mt-8">
        <h3 className="text-xl font-bold text-lime-600 mb-4">
          Official Race Jersey
        </h3>
        <motion.div
          className="max-w-2xl mx-auto w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img 
            src="/jersey/jerseyjotr2026.png" 
            alt="JOTR 2026 Jersey and Racepack" 
            className="w-full h-auto"
          />
        </motion.div>
      </div>
    </div>
  );
};
